import { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { api } from "../lib/api";
import { featureFlags } from "@/config/featureFlags";
import { isSchoolMode } from "@/lib/school";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Mail, ArrowLeft, Lock, Eye, EyeOff } from "lucide-react";
import logo from "@/assets/ithopu-logo.png";
import iTHOPULogo from "@/assets/iTHOPU.png";

export default function Register() {
  // In school mode, registration is not allowed - redirect to login
  if (isSchoolMode()) {
    return <Navigate to="/login" replace />;
  }

  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [role, setRole] = useState<"student" | "parent" | "teacher">("student");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [registrationMethod, setRegistrationMethod] = useState<
    "password" | "otp"
  >("password");
  const navigate = useNavigate();

  const handlePendingCheckout = async () => {
    if (!featureFlags.CHARGEBEE_BILLING) return false;
    const pendingPlan = sessionStorage.getItem("checkout_plan");
    if (pendingPlan) {
      sessionStorage.removeItem("checkout_plan");
      try {
        const { url } = await api.createCheckoutSession(pendingPlan);
        window.location.href = url;
        return true;
      } catch {
        // If checkout fails, just go to dashboard
      }
    }
    return false;
  };

  const handlePasswordRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("=== handlePasswordRegister called ===");
    console.log("Email:", email);
    console.log("Username:", username);
    console.log("Role:", role);

    if (!email || !username || !password || !confirmPassword) {
      setError("Please fill in all fields");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            username,
            role,
          },
        },
      });

      if (signUpError) throw signUpError;

      console.log("User registered successfully!", data);
      setSuccess("Account created successfully! Redirecting...");

      // Wait for profile creation
      await new Promise((resolve) => setTimeout(resolve, 2000));
      // Check for pending checkout before navigating
      const redirected = await handlePendingCheckout();
      if (!redirected) navigate("/dashboard");
    } catch (err) {
      console.error("Registration error:", err);
      setError(err instanceof Error ? err.message : "Failed to register");
    } finally {
      setLoading(false);
    }
  };

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("=== handleSendOTP called ===");
    console.log("Email:", email);
    console.log("Username:", username);
    console.log("Role:", role);

    if (!email || !username) {
      console.log("Validation failed - missing email or username");
      setError("Please enter email and username");
      return;
    }

    setError("");
    setSuccess("");
    setLoading(true);
    console.log("Sending OTP for registration...");

    try {
      const { error: otpError } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: true,
          data: {
            username,
            role,
          },
        },
      });

      if (otpError) throw otpError;

      console.log("OTP sent successfully!");
      setSuccess("Check your email for the verification code!");
      setOtpSent(true);
    } catch (err) {
      console.error("OTP error caught:", err);
      console.error(
        "Error message:",
        err instanceof Error ? err.message : String(err),
      );
      setError(err instanceof Error ? err.message : "Failed to send OTP");
    } finally {
      console.log("OTP send process complete, setting loading to false");
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    console.log("=== handleVerifyOTP called ===");
    console.log("Email:", email);
    console.log("OTP length:", otp?.length);

    if (!otp) {
      console.log("Validation failed - missing OTP");
      setError("Please enter the verification code");
      return;
    }

    setError("");
    setSuccess("");
    setLoading(true);
    console.log("Verifying OTP...");

    try {
      const { error: verifyError } = await supabase.auth.verifyOtp({
        email,
        token: otp,
        type: "email",
      });

      if (verifyError) throw verifyError;

      console.log(
        "OTP verified successfully! Account created. Waiting for profile creation...",
      );

      // Wait a moment for the database trigger to create the profile
      await new Promise((resolve) => setTimeout(resolve, 2000));

      console.log("Checking for pending checkout...");
      const redirected = await handlePendingCheckout();
      if (!redirected) {
        console.log("Navigating to dashboard...");
        navigate("/dashboard");
      }
    } catch (err) {
      console.error("Verify error caught:", err);
      console.error(
        "Error message:",
        err instanceof Error ? err.message : String(err),
      );
      setError(
        err instanceof Error ? err.message : "Invalid verification code",
      );
    } finally {
      console.log("OTP verify process complete, setting loading to false");
      setLoading(false);
    }
  };

  const handleBack = () => {
    setOtpSent(false);
    setOtp("");
    setError("");
    setSuccess("");
  };

  const handleGoogleRegister = async () => {
    console.log("=== handleGoogleRegister called ===");
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const { error: googleError } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: sessionStorage.getItem("checkout_plan")
            ? `${window.location.origin}/checkout/success`
            : `${window.location.origin}/dashboard`,
          queryParams: {
            access_type: "offline",
            prompt: "consent",
          },
        },
      });

      if (googleError) throw googleError;

      // OAuth redirect will happen automatically
      console.log("Google OAuth initiated for registration");
    } catch (err) {
      console.error("Google registration error:", err);
      setError(
        err instanceof Error ? err.message : "Failed to register with Google",
      );
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && otpSent) {
      handleVerifyOTP();
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 py-8">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 flex flex-col items-center">
          <div className="flex items-center gap-2 mb-2">
            <img src={logo} alt="Ithopu" className="w-12 h-12" />
            <img src={iTHOPULogo} alt="Ithopu" className="h-10" />
          </div>
          <CardDescription>
            {otpSent
              ? "Enter your verification code"
              : "Create an account to start your math journey"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded">
              {error}
            </div>
          )}
          {success && (
            <div className="p-3 text-sm text-green-600 bg-green-50 border border-green-200 rounded flex items-start gap-2">
              <Mail className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{success}</span>
            </div>
          )}

          {!otpSent ? (
            <>
              <Tabs
                value={registrationMethod}
                onValueChange={(v) =>
                  setRegistrationMethod(v as "password" | "otp")
                }
                className="w-full"
              >
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="password">
                    <Lock className="w-4 h-4 mr-2" />
                    Password
                  </TabsTrigger>
                  <TabsTrigger value="otp">
                    <Mail className="w-4 h-4 mr-2" />
                    Email Code
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="password">
                  <form
                    onSubmit={handlePasswordRegister}
                    className="space-y-4 mt-4"
                  >
                    <div className="space-y-2">
                      <Label htmlFor="email-password">Email</Label>
                      <Input
                        id="email-password"
                        type="email"
                        placeholder="name@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        disabled={loading}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="username-password">Username</Label>
                      <Input
                        id="username-password"
                        type="text"
                        placeholder="Choose a username"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        required
                        disabled={loading}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="password">Password</Label>
                      <div className="relative">
                        <Input
                          id="password"
                          type={showPassword ? "text" : "password"}
                          placeholder="At least 6 characters"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          disabled={loading}
                          minLength={6}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                          {showPassword ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirm-password">Confirm Password</Label>
                      <div className="relative">
                        <Input
                          id="confirm-password"
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="Re-enter password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          required
                          disabled={loading}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>I am a</Label>
                      <RadioGroup
                        value={role}
                        onValueChange={(value) =>
                          setRole(value as "student" | "parent" | "teacher")
                        }
                        disabled={loading}
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="student" id="student" />
                          <Label
                            htmlFor="student"
                            className="font-normal cursor-pointer"
                          >
                            Student
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="parent" id="parent" />
                          <Label
                            htmlFor="parent"
                            className="font-normal cursor-pointer"
                          >
                            Parent/Guardian
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="teacher" id="teacher" />
                          <Label
                            htmlFor="teacher"
                            className="font-normal cursor-pointer"
                          >
                            Teacher
                          </Label>
                        </div>
                      </RadioGroup>
                    </div>
                    <Button type="submit" className="w-full" disabled={loading}>
                      {loading ? "Creating Account..." : "Create Account"}
                    </Button>
                  </form>
                </TabsContent>

                <TabsContent value="otp">
                  <form onSubmit={handleSendOTP} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="email-otp">Email</Label>
                      <Input
                        id="email-otp"
                        type="email"
                        placeholder="name@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        disabled={loading}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="username-otp">Username</Label>
                      <Input
                        id="username-otp"
                        type="text"
                        placeholder="Choose a username"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        required
                        disabled={loading}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>I am a</Label>
                      <RadioGroup
                        value={role}
                        onValueChange={(value) =>
                          setRole(value as "student" | "parent" | "teacher")
                        }
                        disabled={loading}
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="student" id="student-otp" />
                          <Label
                            htmlFor="student-otp"
                            className="font-normal cursor-pointer"
                          >
                            Student
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="parent" id="parent-otp" />
                          <Label
                            htmlFor="parent-otp"
                            className="font-normal cursor-pointer"
                          >
                            Parent/Guardian
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="teacher" id="teacher-otp" />
                          <Label
                            htmlFor="teacher-otp"
                            className="font-normal cursor-pointer"
                          >
                            Teacher
                          </Label>
                        </div>
                      </RadioGroup>
                    </div>
                    <p className="text-xs text-gray-500">
                      We'll send you a verification code to complete
                      registration
                    </p>
                    <Button type="submit" className="w-full" disabled={loading}>
                      {loading ? "Sending..." : "Send Verification Code"}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>

              {/* Google Sign-In Separator */}
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-gray-500">
                    Or continue with
                  </span>
                </div>
              </div>

              {/* Google Sign-In Button */}
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handleGoogleRegister}
                disabled={loading}
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                {loading ? "Signing up..." : "Sign up with Google"}
              </Button>
            </>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="otp">Verification Code</Label>
              <Input
                id="otp"
                type="text"
                placeholder="Enter 6-digit code"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={loading}
                maxLength={6}
                className="text-center text-lg tracking-widest"
              />
              <p className="text-xs text-gray-500 mt-1">
                Check your email for the verification code
              </p>
            </div>
          )}
        </CardContent>
        <CardFooter className="flex flex-col space-y-4">
          {otpSent && (
            <div className="w-full space-y-2">
              <Button
                type="button"
                onClick={handleVerifyOTP}
                className="w-full"
                disabled={loading}
              >
                {loading ? "Verifying..." : "Verify & Create Account"}
              </Button>
              <Button
                type="button"
                onClick={handleBack}
                variant="ghost"
                className="w-full"
                disabled={loading}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </div>
          )}
          <p className="text-sm text-center text-gray-600">
            Already have an account?{" "}
            <Link to="/login" className="text-indigo-600 hover:underline">
              Login
            </Link>
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}
